import { notification } from "antd"
import JSZip from "jszip"

import { notificationOptions } from "../constants/notification.config"
import { fetchFileBlob, saveBlob } from "./download-file.ts"

export interface ZipDownloadEntry {
  url: string
  name: string
}

const dedupeName = (name: string, count: number) => {
  const dotIndex = name.lastIndexOf(".")
  if (dotIndex <= 0) return `${name} (${count})`
  return `${name.slice(0, dotIndex)} (${count})${name.slice(dotIndex)}`
}

/** Fetches every file's already-authorized URL and assembles them into one
 * zip, client-side — no new backend endpoint and no new authorization
 * surface, since each URL here was already individually authorized by the
 * list call that produced it. Chosen deliberately over a server-side zip
 * endpoint: this repo's prod gunicorn has a 120s request timeout, and a
 * synchronous server-side zip of several S3-backed files (fetch + compress,
 * all inside one request/worker) can plausibly exceed it under load while
 * also holding the full set of file bytes in that worker's memory for the
 * whole request. A name collision (two files sharing one name, e.g. from two
 * different projects) is disambiguated with a trailing " (2)", " (3)", etc.
 * A file that fails to download is skipped rather than failing the whole
 * zip — the user still gets everything that succeeded. */
export const downloadFilesAsZip = async (
  entries: ZipDownloadEntry[],
  zipName: string
) => {
  if (!entries.length) return

  try {
    const zip = new JSZip()
    const usedNames = new Map<string, number>()
    const results = await Promise.allSettled(
      entries.map((entry) =>
        fetchFileBlob(entry.url).then((blob) => ({ entry, blob }))
      )
    )

    let succeeded = 0
    for (const result of results) {
      if (result.status !== "fulfilled") continue
      const { entry, blob } = result.value
      const count = usedNames.get(entry.name) ?? 0
      usedNames.set(entry.name, count + 1)
      zip.file(count === 0 ? entry.name : dedupeName(entry.name, count), blob)
      succeeded += 1
    }

    if (succeeded === 0) {
      throw new Error("None of the selected files could be downloaded.")
    }

    const blob = await zip.generateAsync({ type: "blob" })
    const filename = zipName.endsWith(".zip") ? zipName : `${zipName}.zip`
    saveBlob(blob, filename)

    if (succeeded < entries.length) {
      notification.warning({
        message: `${succeeded} of ${entries.length} files were added to ${filename}; the rest failed to download.`,
        ...notificationOptions.warning
      })
    } else {
      notification.success({
        message: `${filename} downloaded successfully.`,
        ...notificationOptions.success
      })
    }
  } catch (error) {
    console.error(`Error building zip download: ${error}`)
    notification.error({
      message: "Failed to download files.",
      ...notificationOptions.error
    })
  }
}
